"use client"

import {
  ServicePillar,
  BookingAppointment,
  LocationId,
  LOCATIONS,
  getLocationPricing,
  formatPrice,
  formatDuration,
} from "@/data/booking-appointments"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Calendar, MapPin, Clock, CreditCard, CalendarPlus } from "lucide-react"

interface SummaryStepProps {
  pillar: ServicePillar | null
  appointment: BookingAppointment
  location: LocationId
  onBookNow: () => void
  onBack: () => void
}

/**
 * SummaryStep - Display booking summary and final CTA
 *
 * Shows appointment details, location, duration, and price.
 * "Book Date & Time" opens Jane.app immediately in a new tab.
 */
export function SummaryStep({
  pillar,
  appointment,
  location,
  onBookNow,
  onBack,
}: SummaryStepProps) {
  const pricing = getLocationPricing(appointment, location)

  if (!pricing) return null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <div className="text-center">
          <h2 className="text-xl font-normal text-[#3f4141] mb-2 mt-6">
            Your appointment 
          </h2>
        </div>
      </div>

      {/* Summary Card */}
      <div className="bg-gray-100 rounded-xl p-5 space-y-4 mx-4 md:mx-10">
        {/* Appointment Name */}
        <div className="flex items-start gap-3">
          <Calendar className="h-5 w-5 text-[#848974] mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-gray-900">{appointment.name}</p>
            {pillar && (
              <p className="text-sm text-gray-500">{pillar.name}</p>
            )}
          </div>
        </div>

        {/* Location */}
        <div className="flex items-start gap-3">
          <MapPin className="h-5 w-5 text-[#848974] mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-gray-900">{LOCATIONS[location]}</p>
          </div>
        </div>

        {/* Duration */}
        <div className="flex items-start gap-3">
          <Clock className="h-5 w-5 text-[#848974] mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-gray-900">{formatDuration(appointment.duration)}</p>
          </div>
        </div>

        {/* Price */}
        <div className="flex items-start gap-3">
          <CreditCard className="h-5 w-5 text-[#848974] mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-gray-900">{formatPrice(pricing.price)}</p>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <p className="text-sm text-gray-600 text-center leading-relaxed pt-4">
        Select the <strong>date and time</strong> that suits you best on our booking calendar.
      </p>

      {/* Book Date & Time Button */}
      <div className="flex justify-center">
        <Button
          onClick={onBookNow}
          className="w-48 h-10 text-sm bg-[#848974] hover:bg-[#6e7360] text-white flex items-center gap-2"
        >
          <CalendarPlus className="h-4 w-4" />
          Book Date & Time
        </Button>
      </div>
    </div>
  )
}
